//Modified version of TipTap Link plugin to support pasting emails when mentioning users in comments.
//The original plugin will close mention suggestion list on email paste because it converts emails to mailto hrefs.
//Original source code can be found here: https://github.com/ueberdosis/tiptap/tree/main/packages/extension-link
import { Mark, markPasteRule, mergeAttributes, } from "@tiptap/core";
import { find, registerCustomProtocol, reset } from "linkifyjs";
import { autolink } from "./helpers/autolink.js";
import { clickHandler } from "./helpers/clickHandler.js";
import { pasteHandler } from "./helpers/pasteHandler.js";
export const pasteRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)/gi;
// From DOMPurify
// https://github.com/cure53/DOMPurify/blob/main/src/regexp.js
const ATTR_WHITESPACE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g; // eslint-disable-line no-control-regex
const IS_ALLOWED_URI = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i; // eslint-disable-line no-useless-escape
function isAllowedUri(uri) {
    return !uri || uri.replace(ATTR_WHITESPACE, "").match(IS_ALLOWED_URI);
}
/**
 * This extension allows you to create links.
 * @see https://www.tiptap.dev/api/marks/link
 */
export const Link = Mark.create({
    name: "link",
    priority: 1000,
    keepOnSplit: false,
    exitable: true,
    onCreate() {
        this.options.protocols.forEach((protocol) => {
            if (typeof protocol === "string") {
                registerCustomProtocol(protocol);
                return;
            }
            registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
        });
    },
    onDestroy() {
        reset();
    },
    inclusive() {
        return this.options.autolink;
    },
    addOptions() {
        return {
            openOnClick: true,
            linkOnPaste: true,
            autolink: true,
            protocols: [],
            defaultProtocol: "http",
            HTMLAttributes: {
                target: "_blank",
                rel: "noopener noreferrer nofollow",
                class: null,
            },
            validate: (url) => !!url,
        };
    },
    addAttributes() {
        return {
            href: {
                default: null,
            },
            target: {
                default: this.options.HTMLAttributes.target,
            },
            rel: {
                default: this.options.HTMLAttributes.rel,
            },
            class: {
                default: this.options.HTMLAttributes.class,
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: "a[href]",
                getAttrs: (dom) => {
                    const href = dom.getAttribute("href");
                    // prevent XSS attacks
                    if (!href || !isAllowedUri(href)) {
                        return false;
                    }
                    return { href };
                },
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        // prevent XSS attacks
        if (!isAllowedUri(HTMLAttributes.href)) {
            // strip out the href
            return [
                "a",
                mergeAttributes(this.options.HTMLAttributes, Object.assign(Object.assign({}, HTMLAttributes), { href: "" })),
                0,
            ];
        }
        return [
            "a",
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            0,
        ];
    },
    addCommands() {
        return {
            setLink: (attributes) => ({ chain }) => {
                return chain()
                    .setMark(this.name, attributes)
                    .setMeta("preventAutolink", true)
                    .run();
            },
            toggleLink: (attributes) => ({ chain }) => {
                return chain()
                    .toggleMark(this.name, attributes, {
                    extendEmptyMarkRange: true,
                })
                    .setMeta("preventAutolink", true)
                    .run();
            },
            unsetLink: () => ({ chain }) => {
                return chain()
                    .unsetMark(this.name, { extendEmptyMarkRange: true })
                    .setMeta("preventAutolink", true)
                    .run();
            },
        };
    },
    addPasteRules() {
        return [
            markPasteRule({
                find: (text) => {
                    var _a;
                    const foundLinks = [];
                    if (text) {
                        const { validate } = this.options;
                        const links = find(text).filter((item) => item.isLink && validate(item.value));
                        if (links.length) {
                            links.forEach((link) => foundLinks.push({
                                text: link.value,
                                data: {
                                    href: link.href,
                                },
                                index: link.start,
                            }));
                        }
                    }
                    if ((_a = this.editor.view.state["mention$"]) === null || _a === void 0 ? void 0 : _a.active) {
                        return [];
                    }
                    else
                        return foundLinks;
                },
                type: this.type,
                getAttributes: (match) => {
                    var _a;
                    return {
                        href: (_a = match.data) === null || _a === void 0 ? void 0 : _a.href,
                    };
                },
            }),
        ];
    },
    addProseMirrorPlugins() {
        const plugins = [];
        if (this.options.autolink) {
            plugins.push(autolink({
                type: this.type,
                defaultProtocol: this.options.defaultProtocol,
                validate: this.options.validate,
            }));
        }
        if (this.options.openOnClick) {
            plugins.push(clickHandler({
                type: this.type,
            }));
        }
        if (this.options.linkOnPaste) {
            plugins.push(pasteHandler({
                editor: this.editor,
                defaultProtocol: this.options.defaultProtocol,
                type: this.type,
            }));
        }
        return plugins;
    },
});
